import React, { useEffect, useState } from "react";
import { getCodeData } from "@shared/utils/CommonUtils";

interface SnStepUpProps {
  defaultValue?: any;
  label?: string;
  /** 분류코드. 예: SFA035(긴급도) */
  code?: string;
  /** 분류코드 대신 직접 지정하는 조회 SQL(sys_formctrl.dc_sql) */
  sql?: string;
  onValueChanged?: (value: string) => void;
  isReadOnly?: boolean;
  isEditable?: boolean;
  inputRef?: any;
  isSearchPanel?: boolean;
  onFocus?: (target: string) => void;
}

type StepOption = { code: string; name: string };

/** 기본 회색 — 강조가 필요 없는 값. */
const STEP_PLAIN = "#5B6470";
/**
 * 강조 색은 코드/이름이 아니라 목록 위치로 정한다(마지막이 가장 강함).
 * 예) SFA035 보통/중요/긴급 → 회색 / 주황 / 빨강.
 * 색만으로 뜻을 전하지 않도록 이름 텍스트는 항상 그대로 보여준다.
 */
const stepColor = (index: number, total: number) => {
  if (index < 0 || total <= 1) return STEP_PLAIN;
  if (index === total - 1) return "#D32F2F";
  if (index === total - 2) return "#ED6C02";
  return STEP_PLAIN;
};

/**
 * 분류코드 값을 버튼 하나로 순환시키는 컨트롤.
 * 누를 때마다 다음 코드로 넘어가고 마지막에서 첫 값으로 돌아온다.
 * 선택지가 적고(3~5개) 자주 바꾸는 값에 쓴다 — 콤보처럼 목록을 펼칠 필요가 없다.
 *
 * 값은 코드, 화면에 보이는 건 이름이다. 코드에 해당하는 이름이 없으면 코드를 그대로 보여준다.
 * defaultValue 동기화로는 onValueChanged 를 부르지 않는다 —
 * 표시 동기화가 값 변경으로 둔갑하면 그리드 행을 덮어써 버린다.
 */
const SnStepUp = React.memo(
  ({
    defaultValue,
    label,
    code,
    sql,
    onValueChanged,
    isReadOnly = false,
    isEditable = true,
    inputRef,
    onFocus,
  }: SnStepUpProps) => {
    const [options, setOptions] = useState<StepOption[]>([]);
    // 숫자 컬럼도 들어오므로 항상 문자열로 다룬다.
    const [value, setValue] = useState(String(defaultValue ?? ""));

    useEffect(() => {
      setValue(String(defaultValue ?? ""));
    }, [defaultValue]);

    useEffect(() => {
      const fetchOptions = async () => {
        try {
          const data: any = await getCodeData(code || "", sql || "");
          setOptions(
            Array.isArray(data)
              ? data.map((row: any) => ({
                  code: String(row.code ?? ""),
                  name: String(row.name ?? ""),
                }))
              : []
          );
        } catch (error) {}
      };
      fetchOptions();
    }, [code, sql]);

    const disabled = isReadOnly || !isEditable;
    const index = options.findIndex((option) => option.code === value);
    const current = index < 0 ? undefined : options[index];
    const color = disabled ? STEP_PLAIN : stepColor(index, options.length);

    const step = () => {
      if (disabled || options.length === 0) return;
      // 목록에 없는 값(index -1)이면 첫 값으로 간다.
      const next = options[(index + 1) % options.length];
      setValue(next.code);
      onValueChanged?.(next.code);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        step();
      } else if (e.key === "Tab") {
        onFocus?.("next");
      }
    };

    return (
      <button
        type="button"
        ref={inputRef}
        onClick={step}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-label={label}
        title={current?.name || value}
        style={{
          height: "30px",
          minWidth: "64px",
          padding: "0 10px",
          border: `1px solid ${color === STEP_PLAIN ? "rgb(186,191,199)" : color}`,
          borderRadius: "4px",
          background: disabled ? "#F1F1F1" : "white",
          color,
          fontSize: "13px",
          fontWeight: color === STEP_PLAIN ? 400 : 600,
          cursor: disabled ? "default" : "pointer",
          textAlign: "center",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {current?.name || value}
      </button>
    );
  }
);

export default SnStepUp;
